import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, MoveRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Room, TaskObject } from "@/types/checklist";
import { useIsMobile } from "@/hooks/use-mobile";

interface MoveTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskObject;
  currentRoomName: string;
  rooms: Room[];
  onMoveTask: (taskText: string, fromRoom: string, toRoom: string) => Promise<void>;
}

const MoveTaskModal = ({
  isOpen,
  onClose,
  task,
  currentRoomName,
  rooms,
  onMoveTask,
}: MoveTaskModalProps) => {
  const [selectedRoom, setSelectedRoom] = useState<string>("");
  const [isMoving, setIsMoving] = useState(false);
  const isMobile = useIsMobile();

  // Filter out the current room and rooms without names
  const availableRooms = rooms.filter(r => r.name.trim() && r.name !== currentRoomName);

  const handleMove = async () => {
    if (!selectedRoom) {
      toast({ title: "Error", description: "Please select a room", variant: "destructive" });
      return;
    }

    if (selectedRoom === currentRoomName) {
      toast({ title: "Error", description: "Cannot move to the same room", variant: "destructive" });
      return;
    }

    setIsMoving(true);
    try {
      await onMoveTask(task.text, currentRoomName, selectedRoom);
      toast({ title: "Success", description: `Task moved to ${selectedRoom}` });
      handleClose();
    } catch (error) {
      console.error("Error moving task:", error);
      toast({ title: "Error", description: "Failed to move task", variant: "destructive" });
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    setSelectedRoom("");
    setIsMoving(false);
    onClose();
  };

  const content = (
    <div className="space-y-4 py-2">
      {/* Task Preview */}
      <div className="p-3 rounded-lg bg-muted/50 border">
        <p className="text-sm text-muted-foreground mb-1">Task</p>
        <p className="font-medium break-words">{task.text}</p>
      </div>

      {/* Current Room (Read-only) */}
      <div className="space-y-2">
        <Label className="text-muted-foreground">Current Room</Label>
        <div className="p-3 rounded-lg bg-muted/30 border">
          <p className="font-medium">{currentRoomName}</p>
        </div>
      </div>

      {/* Target Room Selector */}
      <div className="space-y-2">
        <Label htmlFor="target-room">Move to Room</Label>
        {availableRooms.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No other rooms available. Add more rooms to move this task.
          </p>
        ) : (
          <Select value={selectedRoom} onValueChange={setSelectedRoom}>
            <SelectTrigger id="target-room" className="w-full">
              <SelectValue placeholder="Select a room..." />
            </SelectTrigger>
            <SelectContent>
              {availableRooms.map((room) => (
                <SelectItem key={room.name} value={room.name}>
                  {room.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Info about what will be preserved */}
      <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
        <p className="font-medium mb-1">What will be preserved:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>All reference photos</li>
          <li>All cleaner-uploaded photos</li>
          <li>Photo requirement settings</li>
          <li>Task translations</li>
        </ul>
      </div>
    </div>
  );

  const footerButtons = (
    <div className={`flex ${isMobile ? 'flex-col w-full' : ''} gap-2`}>
      <Button 
        variant="outline" 
        onClick={handleClose} 
        disabled={isMoving}
        className={isMobile ? 'w-full order-2' : ''}
      >
        Cancel
      </Button>
      <Button 
        onClick={handleMove} 
        disabled={isMoving || !selectedRoom || availableRooms.length === 0}
        className={isMobile ? 'w-full order-1' : ''}
      >
        {isMoving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Moving...
          </>
        ) : (
          <>
            <MoveRight className="w-4 h-4 mr-2" />
            Move Task
          </>
        )}
      </Button>
    </div>
  );

  // Mobile: Use bottom drawer
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={handleClose}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="flex items-center gap-2">
              <MoveRight className="w-5 h-5" />
              Move Task
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 overflow-y-auto">
            {content}
          </div>
          <DrawerFooter className="pt-2">
            {footerButtons}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Use dialog
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" aria-describedby="move-task-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MoveRight className="w-5 h-5" />
            Move Task
          </DialogTitle>
          <DialogDescription id="move-task-desc" className="sr-only">Choose a date to move this task to.</DialogDescription>
        </DialogHeader>
        {content}
        <DialogFooter className="gap-2 sm:gap-0">
          {footerButtons}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveTaskModal;
